import {Component, Input, OnDestroy, OnInit, ViewChild, ViewEncapsulation} from '@angular/core';
import * as _ from 'lodash';
import {OnlinePeerConnectionService} from '../../../../services/online/peer-connection/online-peer-connection.service';
import {TestRouteService} from '../../../../services/map/test-route/test-route.service';
import {RoutingServersSettingsApiService} from '../../../../../services/api/routing-servers/routing-servers-settings-api.service';
import {AgGridAngular} from 'ag-grid-angular';
import {MapService} from '../../../../services/map/map/map.service';
import {AcDialogService} from 'ac-infra';
import {TestRouteDetailsDialogComponent} from '../../../test-route-details-dialog/test-route-details-dialog.component';

@Component({
  selector: 'map-summary-test-route',
  templateUrl: './map-summary-test-route.component.html',
  styleUrls: ['./map-summary-test-route.component.less'],
  encapsulation: ViewEncapsulation.None
})
export class MapSummaryTestRouteComponent implements OnInit, OnDestroy {

  constructor(private testRouteService: TestRouteService,
              private routingServerApiService: RoutingServersSettingsApiService,
              private pConsService: OnlinePeerConnectionService,
              private acDialogService: AcDialogService,
              private mapService: MapService) {
  }

  @ViewChild('agGrid', {static: false}) agGrid: AgGridAngular;
  @Input() set isActive(isActive: boolean) {
    if (isActive) {
      this.mapService.drawTestRoute(null);
      this.restoreDrawnPath();
      this.currentTabSelection = 'testRoutes';
    }
  }

  currentTabSelection: any;
  testRouteResult: any = {};
  testRouteRequest: any;
  testRoutePathSelected: any;
  columnDefs: any;
  gridOptions: any;
  routerName: any;
  routers: any;
  testRouteObservable;
  showUnselectedRules = false;

  selectedPaths = [];
  gridRouteRulesInfo = [];

  selectedPathName = null;


  getRouteRuleRowSpan(currentRoutingRuleName): number {
    const currentGridRouteRuleInfo = this.gridRouteRulesInfo.find(routeRuleItem => currentRoutingRuleName === routeRuleItem.name);

    if (!currentGridRouteRuleInfo || currentGridRouteRuleInfo.isUsed) {
      return 1;
    }

    currentGridRouteRuleInfo.isUsed = true;

    return currentGridRouteRuleInfo.numberOfRows;
  }

  ngOnInit(): void {
    this.columnDefs = [
      {
        headerName: 'Route Rule',
        field: 'routingRuleName',
        rowSpan: (params) => this.getRouteRuleRowSpan(params.data.routingRuleName),
        cellClassRules: {
          'show-cell': (params) => params.data.fork,
          'right-border': 'value !== undefined'
        },
        width: 110,
        tooltipField: 'routingRuleName'
      },
      {
        headerName: 'Path',
        field: 'path',
        width: 70,
        cellClassRules: {
          'hide-content': 'data.unselectedRule'
        }
      },
      {
        headerName: '#Edges',
        field: 'numOfEdges',
        width: 65,
        cellClassRules: {
          'hide-content': 'data.unselectedRule'
        }
      },
      {
        headerName: 'Route Group',
        field: 'routingGroupName',
        width: 100,
        tooltipField: 'routingGroupName'
      }
    ];

    this.gridOptions = {
      defaultColDef: {
        resizable: true
      },
      getRowNodeId: this.getRowNodeId,
      rowSelection: 'multiple',
      suppressCellSelection: true
    };

    this.initialization();

    this.testRouteObservable = this.mapService.onTestRoute().subscribe(({testRouteObject, testRouteResult}) => {
      this.testRoutePathSelected = null;
      sessionStorage.setItem('online_testRoutePathSelected', '');

      this.routerName = this.getRouterName(testRouteResult.routerId);

      this.testRouteService.constructTestRouteData((testRoute) => {
        this.testRouteRequest = testRoute;
        sessionStorage.setItem('online_testRouteRequest', JSON.stringify(testRoute));
      }, testRouteObject);

      this.testRouteService.constructDataForTestRouteGrid((testRoute) => {
        this.clearPreviousPathIfNoRoutesFound(testRoute);
        this.testRouteResult = testRoute;
        sessionStorage.setItem('online_testRouteResult', JSON.stringify(testRoute));
        if (this.agGrid && !testRoute.noRoutesFound) {
          this.initializeTestRouteGrid();
          this.selectFirst();
          this.paintSelectedPaths();
        }

      }, testRouteResult);
    });
  }

  ngOnDestroy(): void {
    this.testRouteObservable.unsubscribe();
  }

  clearTestRoute(): void {
    this.testRouteRequest = null;
    this.testRouteResult = null;
    this.testRoutePathSelected = null;
    sessionStorage.setItem('online_testRouteRequest', '');
    sessionStorage.setItem('online_testRouteResult', '');
    sessionStorage.setItem('online_testRoutePathSelected', '');
    // this.sharedEventService.broadcast('ClearAllPaths');
    this.mapService.drawTestRoute(null);
  }

  repeatTestRoute(): void {
    this.pConsService.testRoute(this.testRouteResult.srcPeerConnection, 'repeat');
  }


  openTestRouteDetailsDialog(): void {
    const testRouteManipulationData = this.testRouteService.constructDataForTestRouteDetailsGrid(this.testRouteResult,
      this.testRoutePathSelected);
    const description = (this.testRouteResult && this.testRouteResult.description) ?
        this.testRouteResult.description : null;

    const dialogData = {
      manipulations: testRouteManipulationData,
      description
    };

    this.acDialogService.open(TestRouteDetailsDialogComponent, {
      dialogData,
      cancelButtonText: 'Ok'
    });
  }

  onGridReady(): void {
    this.restoreTestRouteData();

    if (this.testRouteRequest && this.testRouteResult) {
      this.initializeTestRouteGrid();
      this.restoreSelection();
      this.paintSelectedPaths();
    }
  }

  onSelectionChanged = (selectedRow) => {
    const gridSelectedPaths = this.gridOptions.api.getSelectedRows();
    const indexInGrid = gridSelectedPaths.findIndex(item => selectedRow.data.path === item.path);
    const indexInSelection = this.selectedPaths.findIndex(item => selectedRow.data.path === item.path);


    if (gridSelectedPaths.length === 1) {
      this.selectedPaths = gridSelectedPaths;
    } else if (indexInGrid !== -1 && indexInSelection === -1) {
      this.selectedPaths.push(selectedRow.data);
    } else if (indexInGrid === -1 && indexInSelection !== -1) {
      this.selectedPaths.splice(indexInSelection, 1);
    }

    if (gridSelectedPaths.some(item => (item.routingRuleName !== selectedRow.data.routingRuleName) || !item.fork)) {
      this.gridOptions.api.deselectAll();
      this.gridOptions.api.getRowNode(selectedRow.data.path).setSelected(true);
      this.selectedPaths = this.gridOptions.api.getSelectedRows();
    }

    if (selectedRow.data.fork && (selectedRow.data.routingRuleName === selectedRow.event.target.innerText)) {
      this.selectedPaths = [];

      this.gridOptions.api.forEachNode(node => {
        if (node.data.fork && (node.data.routingRuleName === selectedRow.data.routingRuleName)) {
          node.setSelected(true);
          this.selectedPaths.push(this.gridOptions.api.getRowNode(node.data.path).data);
        }
      });
    }

    this.paintSelectedPaths();
  }

  getRowStyle = (params) => {
    if (params.data.unselectedRule) {
      return { background: '#e2e7d8' };
    }
  }

  private clearPreviousPathIfNoRoutesFound(testRouteResult): void {
    if (this.testRouteResult && !this.testRouteResult.noRoutesFound && testRouteResult.noRoutesFound) {
      // this.sharedEventService.broadcast('ClearAllPaths');
      this.mapService.drawTestRoute(null);
    }
  }

  private selectFirst(): void {
    const rowToSelect = this.selectedPathName ? this.agGrid.api.getRowNode(this.selectedPathName) :
      this.agGrid.api.getDisplayedRowAtIndex(0);
    this.selectedPathName = (rowToSelect.data && rowToSelect.data.unselectedRule) ? null : rowToSelect.data.path;

    if (rowToSelect) {
      rowToSelect.setSelected(true);
      this.selectedPaths = this.gridOptions.api.getSelectedRows();
    }
  }

  private restoreSelection(): void {

    if (this.testRoutePathSelected) {
      this.testRoutePathSelected.forEach(routePath => {
        const selectedRowNode = this.gridOptions.api.getRowNode(routePath.path);
        selectedRowNode.setSelected(true);
      });
    }

    this.selectedPaths = this.gridOptions.api.getSelectedRows();
  }

  private initializeTestRouteGrid(): void {
    const colors = ['#5013B5', '#4B7A9F', '#3DA285', '#1F6A1B'];
    this.gridRouteRulesInfo = [];

    this.testRouteResult.paths.forEach(path => {
      const isRouteRuleInfoExist = this.gridRouteRulesInfo.find(routeRule => {
        return path.routingRuleName === routeRule.name;
      });

      if (!isRouteRuleInfoExist) {
        this.gridRouteRulesInfo.push({
          name: path.routingRuleName,
          numberOfRows: 1,
          isUsed: false
        });
      } else if (path.fork) {
        path.color = colors[isRouteRuleInfoExist.numberOfRows - 1];
        isRouteRuleInfoExist.numberOfRows += 1;
      }

      if (!path.color) {
        path.color = 'purple';
      }
    });

    const paths = this.showUnselectedRules ? this.testRouteResult.paths : this.testRouteResult.paths.filter(path => !path.unselectedRule);

    this.gridOptions.api.setRowData(paths);
  }

  private restoreDrawnPath(): void {
    if (this.testRouteResult) {
      if (this.testRoutePathSelected) {
        // this.sharedEventService.broadcast('testRouteRefresh', this.preparePathsForDraw(this.testRoutePathSelected));
        this.mapService.drawTestRoute(this.preparePathsForDraw(this.testRoutePathSelected));
      }
    }
  }

  private getRouterName(routerId): any {
    return this.routers.find((router) => {
      return router.id === routerId;
    }).name;
  }

  private restoreTestRouteData(): void {
    const request = sessionStorage.getItem('online_testRouteRequest');
    const result = sessionStorage.getItem('online_testRouteResult');
    const selectedPath = sessionStorage.getItem('online_testRoutePathSelected');
    this.showUnselectedRules = sessionStorage.getItem('online_testRouteShowUnselectedRules') === 'true';

    this.testRouteRequest = _.isEqual(request, '') ? null : JSON.parse(request);
    this.testRouteResult = _.isEqual(result, '') ? null : JSON.parse(result);
    this.testRoutePathSelected = _.isEqual(selectedPath, '') ? null : JSON.parse(selectedPath);

    if (this.testRoutePathSelected && this.testRoutePathSelected[0] && !this.testRoutePathSelected[0].unselectedRule) {
      this.selectedPathName = this.testRoutePathSelected[0].path;
    } else {
      this.selectedPathName = null;
    }
  }

  private preparePathsForDraw(testRoutePaths): any {
    if (testRoutePaths.some(route => route.unselectedRule)) {
      return [];
    }

    const pathsForDraw = [];

    testRoutePaths.forEach(pathBlock => {
      if (!pathBlock.discarded) {
        pathsForDraw.push({
          nodes: this.prepareNodesToDraw(pathBlock),
          edges: this.prepareEdgesToDraw(pathBlock),
          color: pathBlock.color
        });
      }
    });

    return pathsForDraw;
  }

  private prepareEdgesToDraw(topRoutePath): any {
    const edges = [];

    edges.push('peerConnection_' + topRoutePath.srcPeerConnection);

    if (topRoutePath.edges !== null) {
      topRoutePath.edges.forEach((edge) => {
        edges.push('connection_' + edge.connectionId);
      });
    }

    if (topRoutePath.srcPeerConnection !== topRoutePath.destPeerConnection) {
      edges.push('peerConnection_' + topRoutePath.destPeerConnection);
    }
    return edges;
  }

  private prepareNodesToDraw(testRoutePath): any {
    const nodes = [];

    nodes.push('node_' + this.testRouteResult.srcNode);

    if (testRoutePath.edges !== null) {
      testRoutePath.edges.forEach((edge) => {
        nodes.push('node_' + edge.connDestNode);
      });
    }

    if (this.testRouteResult.destNode !== null && nodes.indexOf('node_' + testRoutePath.destNode) === -1) {
      nodes.push('node_' + testRoutePath.destNode);
    }

    return nodes;
  }

  private paintSelectedPaths(): void {
    if (this.selectedPaths.length > 0) {
      sessionStorage.setItem('online_testRoutePathSelected', JSON.stringify(this.selectedPaths));
      this.mapService.drawTestRoute(this.preparePathsForDraw(this.selectedPaths));

      this.testRoutePathSelected = this.selectedPaths;
    } else {
      this.testRoutePathSelected = null;
      sessionStorage.setItem('online_testRoutePathSelected', '');
      this.mapService.drawTestRoute(null);
    }
  }

  private initialization(): void {
    this.routingServerApiService.getRoutingServers().subscribe((routers) => {
      this.routers = routers;
    });
  }

  private getRowNodeId(data): any {
    return data.path;
  }

  customerDataExist(): boolean {
    return this.testRouteResult &&
        (this.testRouteResult.incomingCustomerName || this.testRouteResult.outgoingCustomerName);
  }

  showUnselectedRulesChange(showUnselectedRules: boolean): void {
    sessionStorage.setItem('online_testRouteShowUnselectedRules', String(showUnselectedRules));
    const paths = showUnselectedRules ? this.testRouteResult.paths : this.testRouteResult.paths.filter(path => !path.unselectedRule);
    this.gridOptions.api.setRowData(paths);
    this.selectFirst();
    this.paintSelectedPaths();
  }
}
